import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, buttonRecipe } from "../../../components/core/basic/Button";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import { VStack, HStack, Grid, Box } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useNavigate, Navigate, Link, createFileRoute } from "@tanstack/react-router";
import { WalmartAdvertisingSetup } from "../../../modules/onboarding/walmart/WalmartAdvertisingSetup";
import Loading from "../../../components/core/blocks/Loading";
import ConnectSecondaryChannelWidget from "../../../modules/onboarding/components/ConnectSecondaryChannelWidget";
import { setChannel } from "../../../redux/reducers/channel";
import AdditionalWalmartOrgModal from "../../../components/custom/onboarding/AdditionalWalmartOrgModal";
import WalmartProfilesTable from "../../../components/custom/onboarding/WalmartProfilesTable";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../zodios/client";
import { successToast, errorToast } from "../../../utils/toasts";
import { useCan } from "../../../config/casl/casl";
import UnauthorizedWarning from "../../../components/core/basic/UnauthorizedWarning";
import { useWalmartOnboardingStatus } from "../../../modules/onboarding/walmart/useWalmartOnboardingStatus";
import { useWalmartMarketplaceStatus } from "../../../modules/onboarding/walmart/useWalmartMarketplaceStatus";
import { initWalmartMpOauth } from "../../../modules/onboarding/walmart/initWalmartOAuth";
import { WalmartReauthBanner } from "../../../modules/onboarding/walmart/WalmartReauthBanner";
import { getAccountsOptions } from "../../../modules/admin/api/useAccounts";
import { ShopifySecondaryChannelLink } from "../../../modules/onboarding/shopify/ShopifySecondaryChannelLink";
import { useWalmartOAuthCallbackToast } from "../../../modules/onboarding/walmart/useWalmartOAuthCallbackToast";

const calculateDaysRemaining = (expiresAt: string): number => {
  const expiryDate = new Date(expiresAt);
  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const WalmartSummary = () => {
  const { t } = useTranslation(["onboarding"]);

  const getStatusDisplay = (
    marketplaceStatus:
      | { is_connected: boolean; auth_method: "oauth" | "client_credentials" | null; refresh_token_expires_at?: string }
      | undefined,
  ) => {
    if (!marketplaceStatus?.is_connected) {
      return { text: t(($) => $.onboarding["Not Connected"]), isWarning: false };
    }

    if (marketplaceStatus.auth_method === "client_credentials") {
      return { text: t(($) => $.onboarding["Connected (Legacy)"]), isWarning: false };
    }

    if (marketplaceStatus.refresh_token_expires_at) {
      const daysRemaining = calculateDaysRemaining(marketplaceStatus.refresh_token_expires_at);
      const isWarning = daysRemaining <= 7;
      const text =
        daysRemaining < 0 ?
          t(($) => $.onboarding["Connected (Expired)"])
        : t(($) => $.onboarding["Connected (Expires in {days} days)"], { days: daysRemaining });
      return { text, isWarning };
    }

    return { text: t(($) => $.onboarding["Connected"]), isWarning: false };
  };

  const [showWalmartAdvertiserModal, setShowWalmartAdvertiserModal] = useState(false);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);
  const userCanCreateOrg = useCan("create", "Org");

  const { data: onboardingStatus, isLoading: isWalmartOnboardingDataLoading } = useWalmartOnboardingStatus();
  const { data: marketplaceStatus } = useWalmartMarketplaceStatus();

  const { data: hasAmazonProfiles } = useQuery({
    ...getAccountsOptions("amazon"),
    select: (data) => Object.keys(data.seller).length > 0 || Object.keys(data.vendor).length > 0,
  });

  const refreshListings = useMutation({
    mutationFn: async (region: "eu" | "na" | "fe") => {
      return await apiClient.refreshProductListing({
        custom_api_request: "refresh_product_listing",
        region,
      });
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.onboarding["Refreshing listing images"]),
      });
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.onboarding["Failed to refresh listing images"]), error });
    },
  });

  const disconnectMarketplace = useMutation({
    mutationFn: async () => await apiClient.deleteWalmartMarketplace(undefined),
    onSuccess: () => {
      successToast({ message: t(($) => $.onboarding["Walmart marketplace disconnected"]) });
      void queryClient.invalidateQueries({ queryKey: ["walmart-marketplace-status"] });
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.onboarding["Failed to disconnect Walmart marketplace"]), error });
    },
  });

  useWalmartOAuthCallbackToast();

  if (isWalmartOnboardingDataLoading) {
    return <Loading fullPage={true} />;
  }

  if (channel.currentChannel !== "walmart") {
    return <Navigate to="/user/amzsummary" replace />;
  }

  return (
    <>
      <WalmartReauthBanner />
      {userCanCreateOrg ?
        <PageContainer paddingX="xxlarge">
          <VStack gap="medium" paddingTop="xlarge" textAlign="center">
            <h1 className={css({ margin: 0, color: "#393939", fontSize: "34px", fontWeight: 700 })}>
              {t(($) => $.onboarding["Walmart Onboarding"])}
            </h1>
            <p className={css({ margin: 0, fontSize: "medium" })}>
              {t(($) => $.onboarding["Please confirm your following information is correct"])}
            </p>
          </VStack>

          <VStack gap="large" alignItems="stretch">
            <Box
              display="grid"
              gap="medium"
              alignItems="center"
              gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}
              borderBottom="1px solid {colors.border.default}"
              paddingY="large"
            >
              <VStack gap="small" alignItems="stretch">
                <h3 className={css({ margin: 0, fontSize: "large", fontWeight: 600 })}>
                  {t(($) => $.onboarding["Walmart Advertising Account"])}
                </h3>
                <Grid gap="medium" columns={2}>
                  <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                    {t(($) => $.onboarding["Region:"])}
                  </p>
                  <p className={css({ margin: 0, fontSize: "medium" })}>{t(($) => $.onboarding["North America"])}</p>
                </Grid>
                <Grid gap="medium" columns={2}>
                  <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                    {t(($) => $.onboarding["Your Available Advertising Marketplaces:"])}
                  </p>
                  <p className={css({ margin: 0, fontSize: "medium" })}>{t(($) => $.onboarding["United States"])}</p>
                </Grid>
                {onboardingStatus?.advertiser_id && (
                  <Grid gap="medium" columns={2}>
                    <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                      {t(($) => $.onboarding["Advertiser ID:"])}
                    </p>
                    <p className={css({ margin: 0, fontSize: "medium" })}>{onboardingStatus.advertiser_id}</p>
                  </Grid>
                )}
              </VStack>
              {!onboardingStatus?.advertiser_id_provided && (
                <Button
                  onPress={() => {
                    void navigate({ to: "/onboarding/v2/sellertype" });
                  }}
                  className={css({ alignSelf: "center", justifySelf: "end" })}
                >
                  {t(($) => $.onboarding["Authorize"])}
                </Button>
              )}
              {user.is_staff && (
                <HStack gap="small" alignItems="center" gridColumn="1 / -1" fontSize="medium">
                  <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                    {t(($) => $.onboarding["Actions:"])}
                  </p>
                  <Link to="/user/staff/walmart/profiles" className={buttonRecipe({ variant: "link" })}>
                    {t(($) => $.onboarding["View All Unconnected Walmart Profiles"])}
                  </Link>
                </HStack>
              )}
            </Box>
            {user.organization_type !== "vendor" && (
              <VStack
                gap="small"
                alignItems="stretch"
                borderBottom="1px solid {colors.border.default}"
                paddingY="large"
              >
                <h3 className={css({ margin: 0, fontSize: "large", fontWeight: 600 })}>
                  {t(($) => $.onboarding["Walmart Marketplace Account"])}
                </h3>
                <Box
                  display="grid"
                  gap="medium"
                  alignItems="center"
                  gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}
                >
                  <Grid gap="medium" columns={2}>
                    <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                      {t(($) => $.onboarding["Status:"])}
                    </p>
                    <p
                      className={css({
                        margin: 0,
                        color: getStatusDisplay(marketplaceStatus).isWarning ? "text.error" : undefined,
                        fontSize: "medium",
                      })}
                    >
                      {getStatusDisplay(marketplaceStatus).text}
                    </p>
                  </Grid>
                  <VStack gap="xsmall" alignItems="end">
                    <Button
                      onPress={() => {
                        void initWalmartMpOauth(user.organization_id, {
                          title: t(($) => $.onboarding["Walmart connection failed"]),
                          message: t(($) => $.onboarding["Could not start the Walmart connection. Please try again."]),
                        });
                      }}
                    >
                      {marketplaceStatus?.is_connected ?
                        t(($) => $.onboarding["Reauthorize"])
                      : t(($) => $.onboarding["Connect"])}
                    </Button>
                    {marketplaceStatus?.is_connected && (
                      <ConfirmationModal
                        title={t(($) => $.onboarding["Disconnect Walmart Marketplace"])}
                        confirmationMessage={t(
                          ($) => $.onboarding["Are you sure you want to disconnect your Walmart marketplace account?"],
                        )}
                        confirmButtonText={t(($) => $.onboarding["Disconnect"])}
                        modalButtonText={t(($) => $.onboarding["Disconnect"])}
                        modalButtonProps={{ variant: "link" }}
                        onConfirm={() => disconnectMarketplace.mutate()}
                      />
                    )}
                  </VStack>
                </Box>
                {user.is_staff && (
                  <HStack gap="small" alignItems="center" paddingTop="small" fontSize="medium">
                    <p className={css({ margin: 0, fontSize: "medium", fontWeight: 500 })}>
                      {t(($) => $.onboarding["Actions:"])}
                    </p>
                    <Button
                      variant="link"
                      onPress={() => refreshListings.mutate("na")}
                      isPending={refreshListings.isPending}
                    >
                      {t(($) => $.onboarding["Refresh Product Images"])}
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
            <VStack
              gap="medium"
              alignItems="stretch"
              borderBottom="1px solid {colors.border.default}"
              paddingBottom="large"
            >
              <Box display="grid" gap="medium" alignItems="center" gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}>
                <h3 className={css({ margin: 0, fontSize: "large", fontWeight: 600 })}>
                  {t(($) => $.onboarding["All Walmart Accounts"])}
                </h3>
                <Box alignSelf="center" justifySelf="end">
                  <Button onPress={() => setShowAddOrgModal(true)}>{t(($) => $.onboarding["Add New Account"])}</Button>
                  <AdditionalWalmartOrgModal isOpen={showAddOrgModal} onOpenChange={setShowAddOrgModal} />
                </Box>
              </Box>
              <WalmartProfilesTable />
            </VStack>
            <Box
              display="grid"
              gap="medium"
              alignItems="center"
              gridTemplateColumns={{ base: "1fr", lg: "3fr 1fr" }}
              paddingY="xlarge"
            >
              <VStack gap="small" alignItems="stretch">
                <ConnectSecondaryChannelWidget
                  secondaryChannel={"amazon"}
                  linkToSummaryView={
                    hasAmazonProfiles ? "/user/amzsummary" : (
                      `/onboarding/v2/connect?channel=amazon&secondaryChannel=amazon`
                    )
                  }
                  onClick={() => {
                    if (hasAmazonProfiles) {
                      dispatch(setChannel("amazon"));
                      localStorage.setItem("channel", "amazon");
                    }
                  }}
                />
                <ShopifySecondaryChannelLink />
              </VStack>
              <Button
                onPress={() => {
                  void navigate({ to: "/user/dashboard" });
                }}
                className={css({ alignSelf: "center", justifySelf: "end" })}
              >
                {t(($) => $.onboarding["Go to Dashboard"])}
              </Button>
            </Box>
          </VStack>
          {showWalmartAdvertiserModal && (
            <WalmartAdvertisingSetup
              open={showWalmartAdvertiserModal}
              onClose={() => {
                setShowWalmartAdvertiserModal(false);
              }}
              reauth={true}
            />
          )}
        </PageContainer>
      : <UnauthorizedWarning />}
    </>
  );
};

export const Route = createFileRoute("/_authenticated/user/walmartsummary")({
  component: WalmartSummary,
  staticData: { application: "Admin" },
});
